import React, { useState } from 'react';
import { X, Brain, Settings } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import SuperTrendStrategy from './Strategies/SuperTrendStrategy';
import BollingerBandsStrategy from './Strategies/BollingerBandsStrategy';
import RSIStrategy from './Strategies/RSIStrategy';
import StochasticStrategy from './Strategies/StochasticStrategy';

interface AITradingSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  symbol: string;
  currentPrice?: string;
  selectedStrategy: string;
  onStrategyChange: (strategy: string) => void;
  onConfigUpdate: (config: any) => void;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  isRunning?: boolean;
}

interface Strategy {
  id: string;
  name: string;
  description: string;
  component: React.ReactNode;
  isActive: boolean;
}

const AITradingSettings: React.FC<AITradingSettingsProps> = ({
  isOpen,
  onClose,
  symbol,
  currentPrice,
  selectedStrategy,
  onStrategyChange,
  onConfigUpdate,
  onSignalUpdate,
  isRunning
}) => {
  const { theme } = useTheme();

  // Auto-select LazyB CrazyB (supertrend) when modal opens and no strategy is selected
  React.useEffect(() => {
    if (isOpen && !selectedStrategy) {
      onStrategyChange('supertrend');
    }
  }, [isOpen, selectedStrategy, onStrategyChange]);

  const strategies: Strategy[] = [
    {
      id: 'stochastic',
      name: 'MiAI',
      description: 'Momentum indicator for overbought/oversold conditions with %K and %D lines',
      component: <StochasticStrategy symbol={symbol} currentPrice={currentPrice} onConfigUpdate={onConfigUpdate} onSignalUpdate={onSignalUpdate} isRunning={isRunning} />,
      isActive: true
    },
    {
      id: 'supertrend',
      name: 'LazyB CrazyB',
      description: 'Trend-following strategy based on ATR and price action',
      component: <SuperTrendStrategy symbol={symbol} currentPrice={currentPrice} onConfigUpdate={onConfigUpdate} onSignalUpdate={onSignalUpdate} isRunning={isRunning} />,
      isActive: true
    },
    {
      id: 'bollingerbands',
      name: 'Huge Hug',
      description: 'Mean reversion strategy using price volatility bands',
      component: <BollingerBandsStrategy symbol={symbol} currentPrice={currentPrice} onConfigUpdate={onConfigUpdate} onSignalUpdate={onSignalUpdate} isRunning={isRunning} />,
      isActive: true
    },
    {
      id: 'rsi',
      name: 'AI Assistant',
      description: 'Momentum oscillator strategy for overbought/oversold conditions',
      component: <RSIStrategy symbol={symbol} currentPrice={currentPrice} onConfigUpdate={onConfigUpdate} onSignalUpdate={onSignalUpdate} isRunning={isRunning} />,
      isActive: true
    }
  ];

  const currentStrategy = strategies.find(s => s.id === selectedStrategy);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="theme-bg-quaternary rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 w-[800px] max-w-[90vw] mx-4 h-[74vh] overflow-hidden">
        <div className="py-4 relative h-full flex flex-col">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 theme-text-quaternary hover:theme-text-secondary transition-colors duration-200 z-10"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Header */}
          <div className="px-6 py-3 border-b theme-border mb-4">
            <h3 className="text-lg font-semibold theme-text-primary flex items-center">
              <Brain className="h-5 w-5 mr-2 text-[#2AB7A6]" />
              AI Trading Settings
            </h3>
            <div className="text-sm theme-text-quaternary mt-1">Asset: {symbol}</div>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="flex-1 flex overflow-hidden px-4">
            {/* Left Column - Strategy Selection */}
            <div className="w-1/2 border-r theme-border pr-4 theme-bg-primary">
              <div className="h-full overflow-y-auto">
                <div className="px-4 py-4">
                  <div className="space-y-3">
                    {strategies.map((strategy) => (
                      <button
                        key={strategy.id}
                        onClick={() => onStrategyChange(strategy.id)}
                        className={`w-full p-4 rounded-lg border transition-colors duration-200 text-left ${
                          selectedStrategy === strategy.id
                            ? 'border-[#2AB7A6] bg-[#2AB7A6]/10'
                            : 'theme-border hover:theme-border-secondary theme-bg-secondary'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium theme-text-primary text-xs">{strategy.name}</div>
                            <div className="text-xs theme-text-quaternary mt-1 leading-relaxed">{strategy.description}</div>
                          </div>
                          <div className="flex items-center space-x-2 ml-3">
                            {strategy.isActive && (
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            )}
                            <div className={`w-4 h-4 rounded-full border-2 ${
                              selectedStrategy === strategy.id 
                                ? 'border-[#2AB7A6] bg-[#2AB7A6]' 
                                : 'theme-border'
                            }`}></div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Strategy Configuration */}
            <div className="w-1/2 pl-4 theme-bg-secondary">
              <div className="h-full overflow-y-auto">
                {currentStrategy ? (
                  <div className="px-4 py-4">
                    <h3 className="text-xs font-medium theme-text-primary mb-4">
                      {currentStrategy.name} Configuration
                    </h3>
                    {currentStrategy.component}
                  </div>
                ) : (
                  <div className="px-4 py-4 flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="text-xs theme-text-quaternary">Select a strategy to configure</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer with Save Strategy and Cancel Buttons */}
          <div className="px-6 py-4 border-t theme-border">
            <div className="flex justify-between">
              {/* Save Strategy Button - Left */}
              <button
                onClick={() => {
                  // Save the selected strategy
                  onStrategyChange(selectedStrategy);
                  onClose();
                }}
                className="px-6 py-2 bg-[#2AB7A6] hover:bg-[#2AB7A6]/90 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Save Strategy</span>
              </button>
              
              {/* Cancel Button - Right */}
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AITradingSettings;
